import { useState } from 'react'
import { useNavigate } from 'react-router'

import { useDate, useInitData } from '../../../../../hooks'
import { isCalendarSettingsAvailable } from '../../../../../utils/calendar'
import { useCalendarGrid } from '../../../../CalendarGrid'
import {
  useCalendarActionToken,
  useCalendarItemsLoader,
} from '../../../Calendar.hooks'
import { getIsCalendarTaskDeadline } from '../../../Calendar.utils'
import { CALENDAR_VIEW_SETTINGS_PATH } from '../../CalendarView.constants'
import { getCalendarViewRequestKey } from '../../CalendarView.utils'
import { useCalendarViewAppointmentCreate } from '../useCalendarViewAppointmentCreate'
import { useCalendarViewFollowUpMove } from '../useCalendarViewFollowUpMove'
import { useCalendarViewRelieveDay } from '../useCalendarViewRelieveDay'
import { useCalendarViewTask } from '../useCalendarViewTask'
import { useCalendarViewTaskCreate } from '../useCalendarViewTaskCreate'
import { useCalendarViewTaskMove } from '../useCalendarViewTaskMove'

export function useCalendarView({ filters, initialMode, modes, onChangeMode }) {
  const navigate = useNavigate()

  const { availableSections, sections } = useInitData()

  const [modal, setModal] = useState(null)

  const today = useDate()

  const { getItems } = useCalendarItemsLoader(filters)

  const calendar = useCalendarGrid({
    getItems,
    initialMode,
    isEnabled: true,
    modes,
    requestKey: getCalendarViewRequestKey({ filters, today }),
    onChangeMode,
  })

  const { getActionToken, handleStartAction } = useCalendarActionToken()

  function handleCloseModal() {
    handleStartAction()

    setModal(null)
  }

  const modalParams = {
    getActionToken,
    setModal,
    onClose: handleCloseModal,
    onReload: calendar.handleReload,
  }

  const { handleMoveFollowUp } = useCalendarViewFollowUpMove(modalParams)

  const { handleOpenTaskMove } = useCalendarViewTaskMove(modalParams)

  const { handleOpenRelieveDay } = useCalendarViewRelieveDay(modalParams)

  const { handleChangeTaskStatus, handleOpenTask } =
    useCalendarViewTask(modalParams)

  const { handleClickEmptyCell, handleOpenAppointmentCreate } =
    useCalendarViewAppointmentCreate(modalParams)

  const { handleOpenTaskCreate, isTaskNoteShown } = useCalendarViewTaskCreate({
    ...modalParams,
    calendar,
  })

  // TODO: open an appointment in its detail panel once it exists.
  function handleClickItem(item) {
    if (getIsCalendarTaskDeadline(item)) {
      handleOpenTask(item)
    } else if (item.url) {
      navigate(item.url)
    }
  }

  function handleMoveItem(item, date) {
    handleStartAction()

    if (getIsCalendarTaskDeadline(item)) {
      handleOpenTaskMove({ date, item })
    } else {
      handleMoveFollowUp({ date, dayLoad: calendar.dayLoads[date], item })
    }
  }

  function handleOpenSettings() {
    navigate(CALENDAR_VIEW_SETTINGS_PATH)
  }

  return {
    calendar,
    canOpenSettings: isCalendarSettingsAvailable({
      availableSections,
      sections,
    }),
    handleChangeTaskStatus,
    handleClickEmptyCell,
    handleClickItem,
    handleMoveItem,
    handleOpenAppointmentCreate,
    handleOpenRelieveDay,
    handleOpenSettings,
    handleOpenTask,
    handleOpenTaskCreate,
    handleStartAction,
    isTaskNoteShown,
    modal,
  }
}
